import { call, put, takeEvery, select } from 'redux-saga/effects';
import {
  defineKqlQuery,
  fetchSubmission,
  searchSubmissions,
  createSubmission,
  updateSubmission,
  submitSubmission,
  deleteSubmission,
  generateKey,
} from '@kineticdata/react';
import { C, cloneSubmission } from '@kineticdata/bundle-common';
import { actions, types } from '../modules/settingsAppendix';
import { List } from 'immutable';

import { getDefaultContent } from '../../components/shared/content_manager/defaults';

/**
 * Feches all appendices, retrieving all draft, pending, and active versions.
 * Only a small subset of fields are retrieved to prevent storing the full
 * Content JSON string in state for every record.
 */
export function* fetchAppendicesSaga() {
  const { submissions, error } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_DATASTORE,
    form: C.FORM_SLUG_APPENDIX,
    limit: 1000,
    search: {
      q: defineKqlQuery()
        .in(`values[${C.FIELD_STATUS}]`, 'statuses')
        .end()({
        statuses: ['Draft', 'Pending', 'Active'],
      }),
      include: [
        'details',
        ...C.APPENDIX_PRIMARY_FIELDS.map(f => `values[${f}]`),
        'form',
      ],
    },
  });

  if (error) {
    yield put(actions.fetchAppendicesFailure(error));
  } else {
    yield put(actions.fetchAppendicesSuccess(submissions));
  }
}

/**
 * Fetches the selected appendix, retrieving all draft, pending, and active
 * versions. The latest active version gets set as the current appendix, and
 * the full list is used to update the appendices state with the latest data for
 * this appendix id.
 */
export function* fetchAppendixSaga({ payload }) {
  const searcher = defineKqlQuery()
    .in(`values[${C.FIELD_STATUS}]`, 'statuses')
    .equals(`values[${C.FIELD_APPENDIX_ID}]`, 'id');

  const { submissions, error } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_DATASTORE,
    form: C.FORM_SLUG_APPENDIX,
    limit: 1000,
    search: {
      q: searcher.end()({
        statuses: ['Draft', 'Pending', 'Active'],
        id: payload,
      }),
      include: [
        'details',
        'values',
        'children',
        'children.values',
        'children.details',
        'form',
      ],
    },
  });

  if (error) {
    yield put(actions.fetchAppendixFailure(error));
  } else {
    // Sort returned appendices by most recently updated
    const sortedSubmissions = List(submissions)
      .sortBy(p => p.updatedAt)
      .reverse();
    // Get current appendix as the most recently updated Active one, or just
    // the most recently updated one if there are no Active ones
    const currentAppendix =
      sortedSubmissions.find(s => s.values[C.FIELD_STATUS] === 'Active') ||
      sortedSubmissions.first();

    if (!!currentAppendix) {
      // Modify list of appendices to only have the necessary values to prevent
      // the redux store from having a ton of data due to all the Content
      const list = sortedSubmissions.map(submission => ({
        ...submission,
        values: C.APPENDIX_PRIMARY_FIELDS.reduce(
          (vals, field) => ({ ...vals, [field]: submission.values[field] }),
          {},
        ),
      }));

      yield put(
        actions.fetchAppendixSuccess({
          appendixId: payload,
          record: currentAppendix,
          list,
        }),
      );
    } else {
      yield put(
        actions.fetchAppendixFailure({
          statusCode: 404,
          message: `Unable to locate the ${payload} appendix`,
        }),
      );
    }
  }
}

/**
 * Fetches the selected appendix revision.
 */
export function* fetchAppendixRevisionSaga({
  payload: { revisionId, appendixId },
}) {
  const { submission, error } = yield call(fetchSubmission, {
    id: revisionId,
    include: 'details,values,children,children.values,children.details,form',
  });

  if (error) {
    yield put(actions.fetchAppendixRevisionFailure(error));
  } else if (
    !!appendixId &&
    submission.values[C.FIELD_APPENDIX_ID] !== appendixId
  ) {
    // If the Appendix Id of the revision doesn't match the provided Appendix
    // Id, the URL must have been manipulated.
    yield put(
      actions.fetchAppendixRevisionFailure({
        message: `The selected revision doesn't belong to your current appendix.`,
      }),
    );
  } else {
    // If no current appendix set in state or the revision submission is for a
    // different appendix, fetch the correct current appendix
    const currentAppendix = yield select(
      state => state.settingsAppendix.current,
    );
    if (
      !currentAppendix ||
      currentAppendix.values[C.FIELD_APPENDIX_ID] !==
        submission.values[C.FIELD_APPENDIX_ID]
    ) {
      yield put(actions.fetchAppendixRequest(appendixId));
    }

    yield put(actions.fetchAppendixRevisionSuccess(submission));
  }
}

/**
 * Creates a new appendix in Draft status
 */
export function* createAppendixSaga({ payload = {} }) {
  const { success, failure, complete, values = {} } = payload;

  // Generate a key to use as the Appendix Id
  const appendixId = generateKey(9);
  // TODO verify that key isn't already used, or maybe use submission id as key (set after cretiong)

  const { submission, error } = yield call(createSubmission, {
    kappSlug: C.KAPP_SLUG_DATASTORE,
    formSlug: C.FORM_SLUG_APPENDIX,
    values: {
      Name: `New Appendix ${appendixId}`,
      Type: 'Internal',
      ...values,
      [C.FIELD_APPENDIX_ID]: appendixId,
      Status: 'Draft',
      Content: getDefaultContent(C.FORM_SLUG_APPENDIX),
    },
    include: 'details,values,form',
    completed: false,
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // On success, fetch the appendix so its submissions are included in the list
    yield put(actions.fetchAppendixRequest(appendixId));
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.appendixActionComplete());
}

/**
 * Creates a appendix revision by cloning an existing appendix into Draft status
 */
export function* cloneAppendixSaga({ payload = {} }) {
  const { success, failure, complete, submissionId } = payload;

  const { submission, error } = yield call(cloneSubmission, {
    id: submissionId,
    values: { Status: 'Draft' },
    include: 'details,values,form',
    completed: false,
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission && typeof success === 'function') {
    success(submission);

    // On success, fetch the appendix so its submissions are included in the list
    yield put(
      actions.fetchAppendixRequest(submission.values[C.FIELD_APPENDIX_ID]),
    );
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.appendixActionComplete());
}

/**
 * Updates a appendix to an Expired status. If clone flag is true, first
 * creates a clone of the appendix into draft status.
 */
export function* expireAppendixSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    clone = false,
    refetchItem = false,
  } = payload;

  // If clone flag is true, clone the submission into draft state
  if (clone) {
    const { error } = yield call(cloneSubmission, {
      id: submissionId,
      values: { Status: 'Draft' },
      completed: false,
    });

    if (error) {
      if (typeof failure === 'function') {
        failure(error);
      }
      return;
    }
  }

  const { submission, error } = yield call(updateSubmission, {
    id: submissionId,
    values: { Status: 'Expired', 'Locked By': '' },
    include: 'details,values,form',
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // If the current appendix still exists, refetch the appendix
    if (refetchItem) {
      yield put(
        actions.fetchAppendixRequest(submission.values[C.FIELD_APPENDIX_ID]),
      );
    }
    // Otherwise fetch appendices list to get updated list of all data
    else {
      yield put(actions.fetchAppendicesRequest());
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.appendixActionComplete());
}

/**
 * Updates a portoflio revision, keeping the status as Draft
 */
export function* saveAppendixRevisionSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    updatedAt,
    values,
  } = payload;

  // Retrieve the current submission to get its values and verify that the user
  // had the latest version and isn't overwriting anyones changes.
  const { submission: existingSubmission, error: existingError } = yield call(
    fetchSubmission,
    {
      id: submissionId,
      include: 'details,values,form',
    },
  );

  if (existingError) {
    failure(existingError);
  } else if (existingSubmission.updatedAt !== updatedAt) {
    failure({
      message:
        'This revision has been updated since you last loaded it. Please refresh your revision.',
    });
  } else {
    const profile = yield select(state => state.app.profile);

    const { submission, error } = yield call(updateSubmission, {
      id: submissionId,
      values: { ...values, Status: 'Draft', 'Locked By': profile.username },
      include: 'details,values,children,children.values,children.details,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (submission) {
      if (typeof success === 'function') {
        success(submission);
      }

      // If we updated the current revision, update the revision data
      const revision = yield select(state => state.settingsAppendix.revision);
      if (revision && revision.id === submissionId) {
        yield put(actions.fetchAppendixRevisionSuccess(submission));
      }
      // Otherwise fetch the updated appendix so its submissions are included in the list
      else {
        yield put(
          actions.fetchAppendixRequest(submission.values[C.FIELD_APPENDIX_ID]),
        );
      }
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.appendixActionComplete());
}

/**
 * Submits a appendix revision, changing the status to Pending
 */
export function* submitAppendixRevisionSaga({ payload = {} }) {
  const { success, failure, complete, submissionId, updatedAt } = payload;

  // Retrieve the current submission to get its values and verify that the user
  // is submitting the latest version.
  const { submission: existingSubmission, error: existingError } = yield call(
    fetchSubmission,
    {
      id: submissionId,
      include: 'details,values,form',
    },
  );

  if (existingError) {
    failure(existingError);
  } else if (existingSubmission.updatedAt !== updatedAt) {
    failure({
      message:
        'This revision has been updated since you last loaded it. Please refresh your revision, verify the data, and submit again.',
    });
  } else {
    const { submission, error } = yield call(submitSubmission, {
      id: submissionId,
      values: {
        ...existingSubmission.values,
        Status: 'Pending',
        'Locked By': '',
      },
      page: 'Page 1',
      include: 'details,values,children,children.values,children.details,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (submission) {
      if (typeof success === 'function') {
        success(submission);
      }

      // If we updated the current revision, refetch the revision data
      const revision = yield select(state => state.settingsAppendix.revision);
      if (revision && revision.id === submissionId) {
        yield put(actions.fetchAppendixRevisionSuccess(submission));
      }
      // Otherwise fetch the updated appendix so its submissions are included in the list
      else {
        yield put(
          actions.fetchAppendixRequest(submission.values[C.FIELD_APPENDIX_ID]),
        );
      }
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.appendixActionComplete());
}

/**
 * Locks or unlocks a appendix revision
 */
export function* lockAppendixRevisionSaga({ payload = {} }) {
  const { success, failure, complete, submissionId, unlock = false } = payload;

  const profile = yield select(state => state.app.profile);

  const { submission, error } = yield call(updateSubmission, {
    id: submissionId,
    values: { 'Locked By': unlock ? '' : profile.username },
    include: 'details,values,children,children.values,children.details,form',
  });

  if (error && typeof failure === 'function') {
    failure(error);
  } else if (submission) {
    if (typeof success === 'function') {
      success(submission);
    }

    // If we updated the current revision, update the revision data
    const revision = yield select(state => state.settingsAppendix.revision);
    if (revision && revision.id === submissionId) {
      yield put(actions.fetchAppendixRevisionSuccess(submission));
    }
    // Otherwise fetch the updated appendix so its submissions are included in the list
    else {
      yield put(
        actions.fetchAppendixRequest(submission.values[C.FIELD_APPENDIX_ID]),
      );
    }
  }
  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.appendixActionComplete());
}

/**
 * Deletes a appendix revision
 */
export function* deleteAppendixRevisionSaga({ payload = {} }) {
  const {
    success,
    failure,
    complete,
    submissionId,
    cancel = false,
    refetchItem = false,
  } = payload;

  if (!cancel) {
    const { submission, error } = yield call(deleteSubmission, {
      id: submissionId,
      include: 'details,values,form',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    }
    if (!error) {
      if (typeof success === 'function') {
        success();
      }

      // If the current appendix still exists, refetch the appendix
      if (refetchItem) {
        yield put(
          actions.fetchAppendixRequest(submission.values[C.FIELD_APPENDIX_ID]),
        );
      }
      // Otherwise fetch appendices list to get updated list of all data
      else {
        yield put(actions.fetchAppendicesRequest());
      }
    }
  } else {
    // Retrieve the current submission to get its values so we can update it to
    // a Cancelled status
    const { submission: existingSubmission, error: existingError } = yield call(
      fetchSubmission,
      {
        id: submissionId,
        include: 'details,values,form',
      },
    );

    if (existingError) {
      failure(existingError);
    } else if (existingSubmission.values[C.FIELD_STATUS] !== 'Draft') {
      failure({
        message:
          'This revision status has been updated since you last loaded it. Please refresh your revision.',
      });
    } else {
      const { submission, error } = yield call(submitSubmission, {
        id: submissionId,
        values: {
          ...existingSubmission.values,
          Status: 'Cancelled',
          'Locked By': '',
        },
        page: 'Page 1',
        include: 'details,values,form',
      });

      if (error && typeof failure === 'function') {
        failure(error);
      }
      if (!error) {
        if (typeof success === 'function') {
          success();
        }

        // If the current appendix still exists, refetch the appendix
        if (refetchItem) {
          yield put(
            actions.fetchAppendixRequest(
              submission.values[C.FIELD_APPENDIX_ID],
            ),
          );
        }
        // Otherwise fetch appendices list to get updated list of all data
        else {
          yield put(actions.fetchAppendicesRequest());
        }
      }
    }
  }

  if (typeof complete === 'function') {
    complete();
  }

  yield put(actions.appendixActionComplete());
}

export function* watchSettingsAppendix() {
  yield takeEvery(
    [types.FETCH_APPENDICES_REQUEST, types.FETCH_APPENDICES_BY_ID_REQUEST],
    fetchAppendicesSaga,
  );
  yield takeEvery(types.FETCH_APPENDIX_REQUEST, fetchAppendixSaga);
  yield takeEvery(
    types.FETCH_APPENDIX_REVISION_REQUEST,
    fetchAppendixRevisionSaga,
  );
  yield takeEvery(types.CREATE_APPENDIX_REQUEST, createAppendixSaga);
  yield takeEvery(types.CLONE_APPENDIX_REQUEST, cloneAppendixSaga);
  yield takeEvery(types.EXPIRE_APPENDIX_REQUEST, expireAppendixSaga);
  yield takeEvery(
    types.SAVE_APPENDIX_REVISION_REQUEST,
    saveAppendixRevisionSaga,
  );
  yield takeEvery(
    types.LOCK_APPENDIX_REVISION_REQUEST,
    lockAppendixRevisionSaga,
  );
  yield takeEvery(
    types.DELETE_APPENDIX_REVISION_REQUEST,
    deleteAppendixRevisionSaga,
  );
  yield takeEvery(
    types.SUBMIT_APPENDIX_REVISION_REQUEST,
    submitAppendixRevisionSaga,
  );
}
